import {getMishapInterval, isOfflineMishap, isStatusMishap} from './mappers/fromMishaps'
import {clampInterval} from './time'
import {MishapBlockedJobsCorrelation, PrinterStoryKpis, PrintStoryDoc, TimeWindowMs} from './types'
import {toMillis} from './time'

export const computePrinterKpis = (
  mishaps: PrintStoryDoc[],
  window: TimeWindowMs,
  now = Date.now()
): PrinterStoryKpis => {
  const windowEnd = Math.min(window.endMs, now)
  const windowStart = window.startMs
  const windowDuration = Math.max(1, windowEnd - windowStart)

  const overlapping = mishaps
    .map((mishap) => {
      const interval = getMishapInterval(mishap, now)
      if (!interval) {
        return null
      }
      const clamped = clampInterval(interval.startMs, interval.endMs, windowStart, windowEnd)
      if (!clamped) {
        return null
      }
      return {mishap, interval, clamped}
    })
    .filter(Boolean) as Array<{
    mishap: PrintStoryDoc
    interval: NonNullable<ReturnType<typeof getMishapInterval>>
    clamped: {startMs: number; endMs: number}
  }>

  const offlineOverlaps = overlapping.filter(({mishap}) => isOfflineMishap(mishap))
  const incidentOverlaps = overlapping.filter(
    ({mishap}) => isOfflineMishap(mishap) || isStatusMishap(mishap)
  )

  const totalDowntimeMs = offlineOverlaps.reduce(
    (sum, item) => sum + (item.clamped.endMs - item.clamped.startMs),
    0
  )

  const uptimePercent = Math.max(
    0,
    Math.min(100, (1 - totalDowntimeMs / windowDuration) * 100)
  )

  const offlineStarts = offlineOverlaps
    .map((item) => item.interval.startMs)
    .filter((ms) => ms >= windowStart && ms <= windowEnd)
    .sort((a, b) => a - b)

  let mtbfMs: number | null = null
  if (offlineStarts.length >= 2) {
    const gaps: number[] = []
    for (let i = 1; i < offlineStarts.length; i++) {
      gaps.push(offlineStarts[i] - offlineStarts[i - 1])
    }
    mtbfMs = gaps.reduce((a, b) => a + b, 0) / gaps.length
  }

  const resolvedDurations = mishaps
    .filter(isOfflineMishap)
    .filter((m) => m.resolved === true)
    .map((m) => {
      const start = toMillis(m.startedAtMs) || toMillis(m.createdAt)
      const end = toMillis(m.endedAtMs)
      if (!start || !end) {
        return null
      }
      if (start < windowStart || start > windowEnd) {
        return null
      }
      return end - start
    })
    .filter((v): v is number => typeof v === 'number' && v >= 0)

  const mttrMs =
    resolvedDurations.length > 0
      ? resolvedDurations.reduce((a, b) => a + b, 0) / resolvedDurations.length
      : null

  const last = [...mishaps]
    .map((mishap) => {
      const start = toMillis(mishap.startedAtMs) || toMillis(mishap.createdAt)
      if (!start) {
        return null
      }
      return {
        startedAtMs: start,
        endedAtMs: mishap.resolved ? toMillis(mishap.endedAtMs) || null : null,
        resolved: mishap.resolved === true,
        type: String(mishap.type || ''),
        reason: mishap.reason || mishap.staffMessage,
      }
    })
    .filter(Boolean)
    .sort((a, b) => (b!.startedAtMs || 0) - (a!.startedAtMs || 0))[0]

  return {
    uptimePercent,
    incidentCount: incidentOverlaps.length,
    totalDowntimeMs,
    mtbfMs,
    mttrMs,
    lastIncident: last || null,
  }
}

export const correlateBlockedJobsWithMishaps = (
  mishaps: PrintStoryDoc[],
  printJobs: PrintStoryDoc[],
  now = Date.now()
): MishapBlockedJobsCorrelation[] => {
  return mishaps
    .map((mishap) => {
      const interval = getMishapInterval(mishap, now)
      if (!interval) {
        return null
      }

      if (!isOfflineMishap(mishap) && !isStatusMishap(mishap)) {
        return null
      }

      const deviceId = String(mishap.deviceId || '')
      const blocked = printJobs.filter((job) => {
        if (String(job.deviceId || '') !== deviceId) {
          return false
        }
        if (String(job.status || '').toLowerCase() !== 'blocked') {
          return false
        }
        const jobTs =
          toMillis(job.timestamps?.createdAt) ||
          toMillis(job.publishedAtMs) ||
          toMillis(job.createdAtMs) ||
          toMillis(job.createdAt) ||
          toMillis(job.blockedAt)

        if (!jobTs) {
          return false
        }

        return jobTs >= interval.startMs && jobTs <= interval.endMs
      })

      return {
        mishapId: mishap.id,
        startedAtMs: interval.startMs,
        endedAtMs: interval.endMs,
        blockedJobCount: blocked.length,
        blockedJobIds: blocked.map((j) => String(j.jobId || j.id)),
      }
    })
    .filter(Boolean) as MishapBlockedJobsCorrelation[]
}

export const windowFromDays = (days: number, now = Date.now()): TimeWindowMs => ({
  startMs: now - days * 24 * 60 * 60 * 1000,
  endMs: now,
})
